import type { FunctionComponent, PropsWithChildren } from 'react';
import { useState } from 'react';

import { Key, Scope, useShortcut } from '@trello/keybindings';

import styles from './useInteractiveTooltip.less';

// As soon as AK/Tooltip's target element looses focus/hover, the tooltip disappears.
// AK/Tooltip therefore is not interactive, so we gotta make one.

const FocusBoundary: FunctionComponent<{ callback: () => void }> = ({
  callback,
}) => (
  <div
    onFocus={callback}
    tabIndex={0}
    role="button"
    style={{ width: 0, height: 0, outline: 0 }}
  />
);

export const useInteractiveTooltip = () => {
  const [isTooltipVisible, setTooltipVisibility] = useState(false);
  const hideTooltip = () => setTooltipVisibility(false);
  const showTooltip = () => setTooltipVisibility(true);
  const toggleTooltip = () => setTooltipVisibility(!isTooltipVisible);
  const InteractiveTooltip: FunctionComponent<PropsWithChildren<unknown>> = ({
    children,
  }) =>
    isTooltipVisible ? (
      <div className={styles.interactiveTooltip}>
        <FocusBoundary callback={hideTooltip} />
        <div tabIndex={-1} ref={(el) => el?.focus()} style={{ outline: 0 }}>
          {children}
        </div>
        <FocusBoundary callback={hideTooltip} />
      </div>
    ) : null;

  useShortcut(hideTooltip, {
    scope: Scope.Global,
    key: Key.Escape,
  });

  return {
    hideTooltip,
    showTooltip,
    toggleTooltip,
    InteractiveTooltip,
  };
};
